import React, { useState, useEffect, useContext, useMemo } from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaTimes,
  FaChartLine,
  FaBalanceScale,
  FaDownload,
  FaEye
} from "react-icons/fa";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { ToastContainer, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import { useNavigate } from "react-router-dom";

import { ThemeContext } from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const API_BASE = "https://nissi-india.com/api";

const RateComparisonReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);

  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();

  /* =========================================================
     BUTTON CLASSES
  ========================================================= */

  const buttonClass = `bg-${baseColor}-${baseShade}`;

  const hoverClass = `hover:bg-${baseColor}-700`;

  /* =========================================================
     STATE
  ========================================================= */

  const [showFilters, setShowFilters] = useState(true);

  const [loading, setLoading] = useState(false);

  /* =========================================================
     FILTERS
  ========================================================= */

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);

  const [quotationNo, setQuotationNo] = useState("");

  const [purReqNo, setPurReqNo] = useState("");

  const [store, setStore] = useState("");

  /* =========================================================
     DROPDOWN / DATA
  ========================================================= */

  const [storeOptions, setStoreOptions] = useState([]);

  const [results, setResults] = useState([]);

  /* =========================================================
     PAGINATION
     UI ONLY - DOES NOT CHANGE API DATA
  ========================================================= */

  const PAGE_SIZE = 10;

  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));

  const pagedResults = useMemo(
    () => results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [results, page],
  );

  /* =========================================================
     LOCAL STORAGE VALUES
  ========================================================= */

  const finYearId = localStorage.getItem("finYearId") || "";

  const finYear = localStorage.getItem("finYear") || "";

  const branchId = localStorage.getItem("branchId") || "";

  const branch = (localStorage.getItem("branchType") || "").toUpperCase();

  const userId = localStorage.getItem("userId") || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,

      "x-finyear": finYear,

      "x-branchid": branchId,

      "x-branch": branch,

      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId],
  );

  /* =========================================================
     BRANCH TYPE
  ========================================================= */

  const initialBranchType = branch === "HO" ? "HO" : "Branch";

  const [branchType, setBranchType] = useState(initialBranchType);

  const [location, setLocation] = useState(
    initialBranchType === "HO" ? "HO" : "BRANCH",
  );

  useEffect(() => {
    setBranchType(initialBranchType);

    setLocation(initialBranchType === "HO" ? "HO" : "BRANCH");
  }, [initialBranchType]);

  const handleBranchTypeChange = (value) => {
    setBranchType(value);

    setLocation(value === "HO" ? "HO" : "BRANCH");
  };

  /* =========================================================
     COMMON UI CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${isDark ? "text-slate-300" : "text-slate-700"}
  `;

  /* =========================================================
     CLEAR FILTERS
  ========================================================= */

  const clearFilters = () => {
    setFromDate("");

    setToDate(new Date().toISOString().split("T")[0]);

    setQuotationNo("");

    setPurReqNo("");

    setStore("");

    setResults([]);

    setPage(1);
  };

  /* =========================================================
     FORMAT DATE
  ========================================================= */

  const formatDate = (dateStr) => {
    if (!dateStr) {
      return "-";
    }

    const date = new Date(dateStr);

    return Number.isNaN(date.getTime())
      ? dateStr
      : date.toLocaleDateString("en-GB");
  };

  /* =========================================================
     FORMAT CURRENCY
  ========================================================= */

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") {
      return "-";
    }

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  };

  /* =========================================================
     STATUS CLASS
  ========================================================= */

  const getStatusBadgeClass = (status) => {
    if (status === "Approved") {
      return isDark
        ? "bg-green-900/30 text-green-300"
        : "bg-green-100 text-green-700";
    }

    if (status === "Rejected") {
      return isDark ? "bg-red-900/30 text-red-300" : "bg-red-100 text-red-700";
    }

    return isDark
      ? "bg-yellow-900/30 text-yellow-300"
      : "bg-yellow-100 text-yellow-700";
  };

  /* =========================================================
     COMPARE
  ========================================================= */

  const handleCompare = (item) => {
    const id =
      item?.fld_quotationid ||
      item?.fld_ratecomparisonid ||
      item?.fld_enquiryid ||
      item?.fld_id;

    if (!id) {
      toast.warn("Invalid comparison id");

      return;
    }

    navigate(`/inventory/RateComparisonReport_View?id=${id}`);
  };

  /* =========================================================
     LOAD BRANCH / STORE OPTIONS
  ========================================================= */

  useEffect(() => {
    const fetchStoreOptions = async () => {
      try {
        const res = await axios.post(
          `${API_BASE}/getFromStoreOptions`,
          {
            branchtype: branch || "HO",

            branchid: branchId || 8,
          },
          {
            headers: commonHeaders,
          },
        );

        setStoreOptions(res.data.storeOptions || []);
      } catch (error) {
        console.error("Store options error:", error);

        toast.error("Failed to load branch options");
      }
    };

    fetchStoreOptions();
  }, [branch, branchId, commonHeaders]);

  /* =========================================================
     SEARCH
  ========================================================= */

  const handleSearch = async () => {
    if (!store) {
      toast.warn("Please select a Branch");

      return;
    }

    const params = {
      branchid: store || branchId || "8",

      branchwiseno: quotationNo || "",

      fromdate: fromDate || "",

      todate: toDate || "",

      purreqno: purReqNo || "",

      btype: location || "BRANCH",
    };

    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/getquotationdetails_rpt`, {
        params,
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];

      setResults(data);

      setPage(1);

      toast.success(`Loaded ${data.length} record(s)`);
    } catch (err) {
      console.error("Rate comparison search error:", err);

      toast.error("Failed to fetch rate comparison report");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     VIEW ALL
  ========================================================= */

  const handleViewAll = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/getquotationdetails_rpt`, {
        params: {
          branchid: branchId || "8",

          branchwiseno: "",

          fromdate: "",

          todate: "",

          purreqno: "",

          btype: location || "BRANCH",
        },

        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];

      setResults(data);

      setPage(1);

      toast.success("All records loaded");
    } catch (err) {
      console.error("View all error:", err);

      toast.error("Failed to fetch all rate comparison records");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     PAGE NUMBERS
  ========================================================= */

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from(
        {
          length: totalPages,
        },
        (_, i) => i + 1,
      );
    }

    if (page <= 3) {
      return [1, 2, 3, 4, 5];
    }

    if (page >= totalPages - 2) {
      return [
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [page - 2, page - 1, page, page + 1, page + 2];
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Rate Comparison Report</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* ===================================================
            FILTER CARD
            SAME STYLE AS DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* FILTER HEADER */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >
            {/* TITLE */}

            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FaBalanceScale className="w-5 h-5 text-white" />
              </div>

              <div>
                <h3
                  className={`text-lg font-bold ${
                    isDark ? "text-white" : "text-slate-900"
                  }`}
                >
                  Rate Comparison Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}
                >
                  Search and compare supplier quotation rates
                </p>
              </div>
            </div>

            {/* RIGHT BUTTONS */}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload size={14} />
                Export
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter className="w-3.5 h-3.5" />

                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* =================================================
              FILTER BODY
          ================================================= */}

          {showFilters && (
            <div className="p-6">
              {/* =============================================
                  BRANCH TYPE
              ============================================= */}

              <div className="mb-6">
                <label className={labelClass}>Branch Type</label>

                <div className="flex flex-wrap gap-2">
                  {/* HEAD OFFICE */}

                  <button
                    type="button"
                    onClick={() => handleBranchTypeChange("HO")}
                    disabled={branch !== "HO"}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                      branchType === "HO"
                        ? `${buttonClass} text-white shadow`
                        : isDark
                          ? "bg-slate-800 text-slate-300 border border-slate-600"
                          : "bg-slate-100 text-slate-700 border border-slate-200"
                    } ${
                      branch !== "HO" ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    Head Office
                  </button>

                  {/* BRANCH */}

                  <button
                    type="button"
                    onClick={() => handleBranchTypeChange("Branch")}
                    disabled={branch === "HO"}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                      branchType === "Branch"
                        ? `${buttonClass} text-white shadow`
                        : isDark
                          ? "bg-slate-800 text-slate-300 border border-slate-600"
                          : "bg-slate-100 text-slate-700 border border-slate-200"
                    } ${
                      branch === "HO" ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    Branch
                  </button>
                </div>
              </div>

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* FROM DATE */}

                <div>
                  <label className={labelClass}>From Date</label>

                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label className={labelClass}>To Date</label>

                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                {/* QUOTATION NUMBER */}

                <div>
                  <label className={labelClass}>Quotation No</label>

                  <input
                    type="text"
                    value={quotationNo}
                    onChange={(e) => setQuotationNo(e.target.value)}
                    placeholder="Enter quotation number"
                    className={inputClass}
                  />
                </div>

                {/* PURCHASE REQUISITION */}

                <div>
                  <label className={labelClass}>Purchase Req No</label>

                  <input
                    type="text"
                    value={purReqNo}
                    onChange={(e) => setPurReqNo(e.target.value)}
                    placeholder="Enter purchase req number"
                    className={inputClass}
                  />
                </div>

                {/* BRANCH */}

                <div>
                  <label className={labelClass}>
                    Branch <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={store}
                    onChange={(e) => setStore(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select Branch</option>

                    {storeOptions.map((s) => (
                      <option key={s.fld_storeid} value={s.fld_storeid}>
                        {s.fld_storename}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION BUTTONS
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                {/* CLEAR */}

                <button
                  type="button"
                  onClick={clearFilters}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes size={14} />
                  Clear Filters
                </button>

                {/* VIEW ALL */}

                <button
                  type="button"
                  onClick={handleViewAll}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border transition disabled:opacity-50 ${
                    isDark
                      ? "border-slate-600 text-slate-200 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaEye size={14} />
                  View All
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch size={14} />
                  )}

                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
            SAME STYLE AS DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* RESULT HEADER */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">Rate Comparison Results</h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {results.length} records
                </span>
              </div>
            </div>
          </div>

          {/* =================================================
              LOADING
          ================================================= */}

          {loading ? (
            <div className="py-16 text-center">
              <div
                className={`inline-block w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark ? "text-slate-400" : "text-slate-500"
                }`}
              >
                Loading rate comparison records...
              </p>
            </div>
          ) : (
            <>
              {/* =============================================
                  TABLE
              ============================================= */}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Sl No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Quotation No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Date
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Purchase Req No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Supplier
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Total Amount
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Status
                      </th>

                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        Compare
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDark ? "divide-gray-600" : "divide-gray-200"
                    }`}
                  >
                    {pagedResults.length > 0 ? (
                      pagedResults.map((item, idx) => (
                        <tr
                          key={
                            item?.fld_quotationid ||
                            item?.fld_ratecomparisonid ||
                            item?.fld_enquiryid ||
                            item?.fld_id ||
                            idx
                          }
                          className={`transition ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          {/* SL NO */}

                          <td className="px-4 py-3 font-medium">
                            {(page - 1) * PAGE_SIZE + idx + 1}
                          </td>

                          {/* QUOTATION NO */}

                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {item.fld_quotationno || "-"}
                            </span>
                          </td>

                          {/* DATE */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {formatDate(item.fld_date)}
                          </td>

                          {/* PR NO */}

                          <td className="px-4 py-3">
                            {item.fld_purchasereqno || "-"}
                          </td>

                          {/* SUPPLIER */}

                          <td className="px-4 py-3 font-medium">
                            {item.fld_suppliername || "-"}
                          </td>

                          {/* AMOUNT */}

                          <td
                            className={`px-4 py-3 whitespace-nowrap font-semibold ${
                              isDark ? "text-green-300" : "text-green-700"
                            }`}
                          >
                            {formatCurrency(item.fld_totalamount)}
                          </td>

                          {/* STATUS */}

                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                                item.fld_status || "Pending",
                              )}`}
                            >
                              {item.fld_status || "Pending"}
                            </span>
                          </td>

                          {/* COMPARE */}

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleCompare(item)}
                              className={`p-2 rounded-full transition ${
                                isDark
                                  ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                  : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                              }`}
                              title="View Comparison"
                            >
                              <FaChartLine className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-14 text-center text-gray-500"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <FaBalanceScale
                              size={40}
                              className={
                                isDark ? "text-slate-500" : "text-slate-400"
                              }
                            />

                            <span>No rate comparison records found</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* =============================================
                  PAGINATION
              ============================================= */}

              <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  {/* RECORD INFO */}

                  <div
                    className={`text-sm ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Showing{" "}
                    {results.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} to{" "}
                    {Math.min(page * PAGE_SIZE, results.length)} of{" "}
                    {results.length} results
                  </div>

                  {/* PAGE CONTROLS */}

                  <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                    {/* PREVIOUS */}

                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      disabled={page === 1}
                      className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                        page === 1
                          ? "text-gray-400 cursor-not-allowed"
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                      }`}
                    >
                      <ChevronLeft size={16} />
                      Prev
                    </button>

                    {/* PAGE NUMBERS */}

                    {getPageNumbers().map((pageNo) => (
                      <button
                        type="button"
                        key={pageNo}
                        onClick={() => setPage(pageNo)}
                        className={`px-4 py-2 text-sm font-medium transition ${
                          page === pageNo
                            ? `bg-${baseColor}-${baseShade} text-white`
                            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {pageNo}
                      </button>
                    ))}

                    {/* NEXT */}

                    <button
                      type="button"
                      onClick={() =>
                        setPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={page === totalPages || results.length === 0}
                      className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                        page === totalPages || results.length === 0
                          ? "text-gray-400 cursor-not-allowed"
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                      }`}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default RateComparisonReport;
